import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';
import { Mail, Lock, User as UserIcon, Loader2 } from 'lucide-react';

export default function Auth({ mode = 'login' }) {
  const nav = useNavigate();
  const { login, signup } = useApp();
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !pwd) { toast.error('Email and password required'); return; }
    setLoading(true);
    try {
      let u;
      if (mode === 'login') u = await login(email, pwd);
      else u = await signup(email, pwd, name || undefined);
      toast.success(mode === 'login' ? 'Welcome back!' : 'Account created');
      const dest = u?.profile_type === 'listener' ? '/feed' : (u?.profile_type === 'musician' ? '/reapercast' : '/home');
      nav(dest);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Authentication failed');
    } finally { setLoading(false); }
  };

  const quickDemo = async (role) => {
    setLoading(true);
    try {
      const em = role === 'admin' ? 'admin@vidcreforge.com' : 'creator@vidcreforge.com';
      const u = await login(em, 'vidhub123');
      toast.success('Demo session started');
      nav(u.role === 'admin' ? '/admin' : '/home');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Demo login failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white flex">
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1601944179066-29786cb9d32a?w=1600" alt="" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(10,10,12,0.85), rgba(10,10,12,0.4) 60%, rgba(255,46,99,0.25))' }} />
        <div className="relative p-12 flex flex-col justify-between w-full">
          <Link to="/"><Logo /></Link>
          <div>
            <h2 className="font-display text-[54px] leading-[1] max-w-[460px]">Listen. Create. <span className="brand-gradient">Own your audience.</span></h2>
            <p className="text-white/75 mt-4 max-w-[460px]">ReaperCast is the operating system for independent music — a gamified academy, AI specialists, knowledge hub, and video hosting (VidCreForge) bundled free.</p>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-[520px] flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-[400px]">
          <div className="lg:hidden mb-6"><Link to="/"><Logo /></Link></div>
          <h1 className="font-display text-[36px] leading-none">{mode === 'login' ? 'Welcome back' : 'Create your account'}</h1>
          <p className="text-white/60 text-[14px] mt-2">
            {mode === 'login' ? 'Sign in to continue to ReaperCast.' : 'Start free — upgrade anytime.'}
          </p>
          {mode === 'signup' && (
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Link to="/signup/listener" className="card-shell p-3 hover-lift text-center" data-testid="auth-signup-listener">
                <div className="font-display text-[14px]">I'm a listener</div>
                <div className="text-[11.5px] text-white/55 mt-0.5">Discover music, follow artists</div>
              </Link>
              <Link to="/reapercast/signup" className="card-shell p-3 hover-lift text-center" data-testid="auth-signup-musician">
                <div className="font-display text-[14px]">I'm an artist</div>
                <div className="text-[11.5px] text-white/55 mt-0.5">Release music, earn, learn</div>
              </Link>
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-3">
            {mode === 'signup' && (
              <label className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-lg px-3 focus-within:border-[#ff2e63]/60">
                <UserIcon className="w-4 h-4 text-white/50" />
                <input value={name} onChange={e => setName(e.target.value)} placeholder="Full name" className="bg-transparent flex-1 py-3 focus:outline-none text-[14px] placeholder:text-white/40" data-testid="signup-name-input" />
              </label>
            )}
            <label className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-lg px-3 focus-within:border-[#ff2e63]/60">
              <Mail className="w-4 h-4 text-white/50" />
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="bg-transparent flex-1 py-3 focus:outline-none text-[14px] placeholder:text-white/40" data-testid="login-email-input" />
            </label>
            <label className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-lg px-3 focus-within:border-[#ff2e63]/60">
              <Lock className="w-4 h-4 text-white/50" />
              <input type="password" value={pwd} onChange={e => setPwd(e.target.value)} placeholder="••••••••" className="bg-transparent flex-1 py-3 focus:outline-none text-[14px] placeholder:text-white/40" data-testid="login-password-input" />
            </label>
            <button type="submit" disabled={loading} className="btn-primary w-full mt-2 disabled:opacity-60" data-testid="login-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {mode === 'login' ? 'Sign in' : 'Create account'}
            </button>
            {mode === 'login' && (
              <div className="text-right">
                <Link to="/forgot-password" className="text-[12.5px] text-white/60 hover:text-[#ff6b8a]" data-testid="auth-forgot-link">Forgot password?</Link>
              </div>
            )}
          </form>

          <div className="flex items-center gap-2 my-5 text-white/40 text-[12px]">
            <div className="flex-1 h-px bg-white/10" /> or try a demo <div className="flex-1 h-px bg-white/10" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => quickDemo('creator')} disabled={loading} className="btn-ghost disabled:opacity-60" data-testid="login-creator-demo-btn">Creator demo</button>
            <button onClick={() => quickDemo('admin')} disabled={loading} className="btn-ghost disabled:opacity-60" data-testid="login-admin-demo-btn">Admin demo</button>
          </div>

          <div className="text-[13.5px] text-white/60 mt-6 text-center">
            {mode === 'login' ? (
              <>Don’t have an account? <Link to="/signup" className="text-[#ff6b8a] hover:underline">Sign up</Link></>
            ) : (
              <>Already a member? <Link to="/login" className="text-[#ff6b8a] hover:underline">Sign in</Link></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
